import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { getSelfServiceContentFallback } from '../data/selfServiceContent';
import { hydrateDocumentColumns } from '../data/dashboardContent';
import {
  MENU_COLUMN_DEFS,
  buildMenuTemplateMatrix,
  formatMenuDate,
  getMonthKey,
  normalizeLunchMenu,
  parseMenuSpreadsheetRows
} from '../utils/menuSpreadsheet';

const ContentAdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [selfService, setSelfService] = useState({});
  const [lunchMenu, setLunchMenu] = useState(() => normalizeLunchMenu());
  const [selectedMonth, setSelectedMonth] = useState(() => getMonthKey());
  const [documentColumns, setDocumentColumns] = useState(() => hydrateDocumentColumns());
  const [savingMenu, setSavingMenu] = useState(false);
  const [readingMenuFile, setReadingMenuFile] = useState(false);
  const [menuFileName, setMenuFileName] = useState('');
  const [savingDocs, setSavingDocs] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', message }
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const menuFileInputRef = useRef(null);

  const currentUser = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('userData') || '{}'); }
    catch (error) { return {}; }
  }, []);
  const isSuperAdmin = currentUser.role === 'super_admin';

  const activeCompanyId = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userData') || '{}');
      if (user.role === 'super_admin') {
        return localStorage.getItem('activeCompanyId') || '';
      }
      return user.companyId || '';
    } catch (error) {
      return '';
    }
  }, []);

  useEffect(() => {
    if (!isSuperAdmin) {
      setSelectedCompanyIds(activeCompanyId ? [activeCompanyId] : []);
      return;
    }

    let active = true;
    api.getCompanies()
      .then((response) => {
        if (!active) return;
        const availableCompanies = (response.companies || []).filter((company) => company.isActive !== false);
        setCompanies(availableCompanies);
        setSelectedCompanyIds((current) => current.length ? current : (activeCompanyId ? [activeCompanyId] : []));
      })
      .catch(() => setCompanies([]));
    return () => { active = false; };
  }, [activeCompanyId, isSuperAdmin]);

  const publishToSelectedCompanies = async (section, payload) => {
    const targets = isSuperAdmin ? selectedCompanyIds : [activeCompanyId].filter(Boolean);
    if (!targets.length) throw new Error('Yayınlanacak en az bir firma seçin.');
    await Promise.all(targets.map((companyId) => api.updateManagedContent(section, {
      payload,
      activeCompanyId: companyId
    })));
    return targets.length;
  };

  useEffect(() => {
    let active = true;

    const loadContent = async () => {
      setLoading(true);
      try {
        const response = await api.getManagedContent();
        if (!active) {
          return;
        }
        const data = response?.data || {};
        const loadedSelfService = data.selfService || {};
        setSelfService(loadedSelfService);
        const savedMonth = loadedSelfService.lunchMenus?.[selectedMonth];
        setLunchMenu(normalizeLunchMenu(savedMonth?.length ? savedMonth : loadedSelfService.lunchMenu || getSelfServiceContentFallback().lunchMenu, selectedMonth));
        setDocumentColumns(hydrateDocumentColumns(data.dashboard?.documentColumns));
      } catch (error) {
        // Henüz firmaya ait yönetilen içerik yoksa varsayılanlarla başla.
        if (active) {
          setSelfService({});
          setLunchMenu(normalizeLunchMenu(getSelfServiceContentFallback().lunchMenu, selectedMonth));
          setDocumentColumns(hydrateDocumentColumns());
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadContent();
    return () => {
      active = false;
    };
  }, [activeCompanyId, selectedMonth]);

  const updateMenuField = (index, field, value) => {
    setLunchMenu((current) =>
      current.map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row))
    );
  };

  const handleDownloadMenuTemplate = async () => {
    setFeedback(null);
    try {
      const XLSX = await import('xlsx');
      const worksheet = XLSX.utils.aoa_to_sheet(buildMenuTemplateMatrix(selectedMonth));
      worksheet['!cols'] = MENU_COLUMN_DEFS.map((column, index) => ({
        wch: index === 0 ? 18 : index === MENU_COLUMN_DEFS.length - 1 ? 20 : 30
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Yemekhane Menusu');
      XLSX.writeFile(workbook, `yemekhane-menusu-${selectedMonth}.xlsx`);
    } catch (error) {
      setFeedback({ type: 'error', message: 'Menü şablonu indirilemedi.' });
    }
  };

  const handleMenuFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setReadingMenuFile(true);
    setFeedback(null);
    try {
      const XLSX = await import('xlsx');
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(firstSheet, { defval: '' });
      const parsedMenu = parseMenuSpreadsheetRows(rows);

      setLunchMenu(parsedMenu);
      setMenuFileName(file.name);
      setFeedback({
        type: 'success',
        message: `${file.name} içindeki aylık menü forma aktarıldı. Kalıcılaştırmak için “Menüyü Kaydet” düğmesine basın.`
      });
    } catch (error) {
      setMenuFileName('');
      setFeedback({
        type: 'error',
        message: error?.message || 'Dosya okunamadı. Lütfen geçerli bir Excel/CSV dosyası seçin.'
      });
    } finally {
      setReadingMenuFile(false);
      if (menuFileInputRef.current) {
        menuFileInputRef.current.value = '';
      }
    }
  };

  const handleSaveMenu = async () => {
    setSavingMenu(true);
    setFeedback(null);
    try {
      const lunchMenus = { ...(selfService.lunchMenus || {}), [selectedMonth]: lunchMenu };
      const targetCount = await publishToSelectedCompanies('selfService', { ...selfService, lunchMenu, lunchMenus });
      setSelfService((current) => ({ ...current, lunchMenu, lunchMenus }));
      setMenuFileName('');
      setFeedback({ type: 'success', message: `Yemekhane menüsü ${targetCount} firmada yayınlandı.` });
    } catch (error) {
      setFeedback({ type: 'error', message: error?.message || 'Menü kaydedilemedi.' });
    } finally {
      setSavingMenu(false);
    }
  };

  const updateColumnField = (colIndex, field, value) => {
    setDocumentColumns((current) =>
      current.map((col, index) => (index === colIndex ? { ...col, [field]: value } : col))
    );
  };

  const updateLinkField = (colIndex, linkIndex, field, value) => {
    setDocumentColumns((current) =>
      current.map((col, index) => {
        if (index !== colIndex) {
          return col;
        }
        return {
          ...col,
          links: col.links.map((link, lIndex) =>
            lIndex === linkIndex ? { ...link, [field]: value } : link
          )
        };
      })
    );
  };

  const addLink = (colIndex) => {
    setDocumentColumns((current) =>
      current.map((col, index) =>
        index === colIndex
          ? { ...col, links: [...col.links, { label: '', path: '', featured: false }] }
          : col
      )
    );
  };

  const removeLink = (colIndex, linkIndex) => {
    setDocumentColumns((current) =>
      current.map((col, index) =>
        index === colIndex
          ? { ...col, links: col.links.filter((_, lIndex) => lIndex !== linkIndex) }
          : col
      )
    );
  };

  const handleSaveDocs = async () => {
    setSavingDocs(true);
    setFeedback(null);
    try {
      const targetCount = await publishToSelectedCompanies('dashboard', { documentColumns });
      setFeedback({ type: 'success', message: `Öne çıkan dokümanlar ${targetCount} firmada yayınlandı.` });
    } catch (error) {
      setFeedback({ type: 'error', message: error?.message || 'Dokümanlar kaydedilemedi.' });
    } finally {
      setSavingDocs(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '360px' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="container-fluid content-admin">
      <div className="row mb-4">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              <h3 className="mb-2">İçerik Yönetimi</h3>
            </div>
          </div>
        </div>
      </div>

      {feedback && (
        <div
          className={`alert ${feedback.type === 'success' ? 'alert-success' : 'alert-danger'}`}
          role="alert"
        >
          {feedback.message}
        </div>
      )}

      {isSuperAdmin && (
        <div className="row mb-4">
          <div className="col-12">
            <fieldset className="box box-body content-publish-targets">
              <legend>Yayınlanacak firmalar</legend>
              <p>Bu seçim hem yemekhane menüsü hem de öne çıkan dokümanlar için kullanılır.</p>
              <div className="content-publish-target-options">
                {companies.map((company) => (
                  <label key={company._id}>
                    <input
                      type="checkbox"
                      checked={selectedCompanyIds.includes(company._id)}
                      onChange={(event) => setSelectedCompanyIds((current) => event.target.checked
                        ? [...current, company._id]
                        : current.filter((id) => id !== company._id))}
                    />
                    <span>{company.name}</span>
                  </label>
                ))}
              </div>
              <small>{selectedCompanyIds.length} firma seçili</small>
            </fieldset>
          </div>
        </div>
      )}

      {/* --- Yemekhane Menüsü --- */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <div>
                  <h4 className="mb-1">Aylık Yemekhane Menüsü</h4>
                  <p className="text-muted mb-0">Pazartesi–Cumartesi menülerini tek ekrandan düzenleyin veya Excel’den aktarın.</p>
                </div>
                <div className="d-flex align-items-center flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={handleDownloadMenuTemplate}
                    disabled={readingMenuFile || savingMenu}
                  >
                    <i className="bx bxs-download me-1" aria-hidden="true"></i>
                    Şablon İndir
                  </button>
                  <input
                    ref={menuFileInputRef}
                    type="file"
                    className="d-none"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleMenuFileChange}
                    aria-label="Yemekhane menüsü Excel veya CSV dosyası"
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => menuFileInputRef.current?.click()}
                    disabled={readingMenuFile || savingMenu}
                  >
                    <i className={`bx ${readingMenuFile ? 'bx-loader-alt bx-spin' : 'bx-upload'} me-1`} aria-hidden="true"></i>
                    {readingMenuFile ? 'Dosya Okunuyor…' : 'Excel / CSV Yükle'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveMenu}
                    disabled={savingMenu || readingMenuFile || (isSuperAdmin && !selectedCompanyIds.length)}
                  >
                    {savingMenu ? 'Kaydediliyor…' : 'Menüyü Kaydet'}
                  </button>
                </div>
              </div>
              <div className="content-admin-month mb-3">
                <label htmlFor="menuMonth">Menü ayı</label>
                <input id="menuMonth" type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} />
                <span>{lunchMenu.length} menü günü</span>
              </div>
              {menuFileName && (
                <p className="small text-muted mb-3">
                  <i className="bx bxs-file me-1" aria-hidden="true"></i>
                  Aktarılan dosya: <strong>{menuFileName}</strong>
                </p>
              )}
              <div className="table-responsive">
                <table className="table align-middle content-admin-table">
                  <thead>
                    <tr>
                      <th>Tarih</th>
                      <th>Gün</th>
                      <th>Çorba</th>
                      <th>Ana Yemek</th>
                      <th>Yan Yemek</th>
                      <th>Ekstra</th>
                      <th>Servis Saati</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lunchMenu.map((row, index) => (
                      <tr key={row.date}>
                        <td className="text-nowrap">{formatMenuDate(row.date)}</td>
                        <td className="fw-semibold">{row.day}</td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.soup}
                            onChange={(event) => updateMenuField(index, 'soup', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.main}
                            onChange={(event) => updateMenuField(index, 'main', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.side}
                            onChange={(event) => updateMenuField(index, 'side', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.extra}
                            onChange={(event) => updateMenuField(index, 'extra', event.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={row.serviceWindow}
                            onChange={(event) =>
                              updateMenuField(index, 'serviceWindow', event.target.value)
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- Öne Çıkan Dokümanlar --- */}
      <div className="row">
        <div className="col-12">
          <div className="box">
            <div className="box-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="mb-0">Öne Çıkan Form / Prosedür / Dokümanlar</h4>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveDocs}
                  disabled={savingDocs || (isSuperAdmin && !selectedCompanyIds.length)}
                >
                  {savingDocs ? 'Kaydediliyor…' : 'Dokümanları Kaydet'}
                </button>
              </div>

              <div className="row g-3">
                {documentColumns.map((column, colIndex) => (
                  <div key={column.key} className="col-lg-4">
                    <div className="content-admin-col">
                      <label className="form-label fw-semibold">Kart Başlığı</label>
                      <input
                        className="form-control mb-3"
                        value={column.title}
                        onChange={(event) =>
                          updateColumnField(colIndex, 'title', event.target.value)
                        }
                      />

                      {column.links.map((link, linkIndex) => (
                        <div key={linkIndex} className="content-admin-link">
                          <input
                            className="form-control form-control-sm mb-1"
                            placeholder="Bağlantı etiketi"
                            value={link.label}
                            onChange={(event) =>
                              updateLinkField(colIndex, linkIndex, 'label', event.target.value)
                            }
                          />
                          <input
                            className="form-control form-control-sm mb-1"
                            placeholder="Bağlantı yolu (ör. /dokuman-merkezi?kategori=...)"
                            value={link.path}
                            onChange={(event) =>
                              updateLinkField(colIndex, linkIndex, 'path', event.target.value)
                            }
                          />
                          <div className="d-flex justify-content-between align-items-center">
                            <label className="form-check-label d-flex align-items-center gap-1">
                              <input
                                type="checkbox"
                                className="form-check-input mt-0"
                                checked={Boolean(link.featured)}
                                onChange={(event) =>
                                  updateLinkField(
                                    colIndex,
                                    linkIndex,
                                    'featured',
                                    event.target.checked
                                  )
                                }
                              />
                              <span className="small">Öne çıkar</span>
                            </label>
                            <button
                              type="button"
                              className="btn btn-sm btn-link text-danger p-0"
                              onClick={() => removeLink(colIndex, linkIndex)}
                            >
                              Kaldır
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary mt-2"
                        onClick={() => addLink(colIndex)}
                      >
                        + Bağlantı ekle
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentAdminPage;
